<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import AssistenciaTecnicaColumnView from '@/components/assistencia/AssistenciaTecnicaColumnView.vue';
import { useAssistenciaTecnicaStore } from '@/stores/assistenciaTecnica';
import useColumnManagement from '@/composables/useColumnManagement';
import { origemOptions, estadosOptions } from '@/utils/filtros.js';

// Initialize store
const assistenciaStore = useAssistenciaTecnicaStore();

// Initialize composables
const {
  columnSettingsMenuOpen,
  draggedColumn,
  onDragStart,
  onDragOver, 
  onDrop,
  toggleColumnVisibility
} = useColumnManagement(assistenciaStore);

// Filtros
const filtrosExpanded = ref([1]);

// Setup & cleanup
const initializeData = async () => {
  try {
    // First initialize the store (this will fetch oldest date and update filters)
    await assistenciaStore.initialize();
    
    // Then fetch assistencias with the updated filters
    await assistenciaStore.fetchAssistencias({ loadMore: false });
    
    // Finally setup the refresh timer
    assistenciaStore.setupRefreshTimer();
  } catch (err) {
    console.error('Erro na inicialização:', err);
  }
};

onMounted(() => {
  initializeData();
});

// Ensure cleanup happens on unmount
onBeforeUnmount(() => {
  console.log('Unmounting AssistenciaTecnicaView, clearing refresh timer');
  assistenciaStore.clearRefreshTimer();
});

// Funções para aplicar e limpar filtros
const aplicarFiltros = () => {
  assistenciaStore.aplicarFiltros();
};

const limparFiltros = () => {
  assistenciaStore.limparFiltros();
};

// Computed properties
const visibleColumns = computed(() => 
  assistenciaStore.columns.filter(column => column.visible)
);

const hasVisibleColumns = computed(() => 
  assistenciaStore.columns.some(column => column.visible)
);

const toggleAllColumns = (value) => {
  assistenciaStore.toggleAllColumns(value);
};

</script>

<template>
  <div>    
    <v-row>
      <v-col cols="12" class="d-flex align-center justify-space-between">
        <h1 class="text-h4 mb-2">Gerenciar Assistências Técnicas</h1>
        
        <!-- Column settings menu -->
        <v-menu
          v-model="columnSettingsMenuOpen"
          :close-on-content-click="false"
          location="bottom end"
        >
          <template v-slot:activator="{ props }">
            <v-btn
              color="primary"
              variant="outlined"
              prepend-icon="mdi-cog-outline"
              v-bind="props"
            >
              Configurar Colunas
            </v-btn>
          </template>
          
          <v-card min-width="320" class="pa-3">
            <v-card-title class="px-0 pt-0">Configurar colunas</v-card-title>
            <v-divider class="my-2"></v-divider>
            
            <p class="text-body-2 mb-3">Arraste para reordenar e clique no ícone para mostrar/ocultar:</p>
                
            <v-list density="compact" class="pa-0">
              <v-list-item
                v-for="(column, index) in assistenciaStore.columns"
                :key="column.key"
                :draggable="true"
                @dragstart="onDragStart(column)"
                @dragover="onDragOver"
                @drop="onDrop(column)"
                class="column-order-item mb-1"
                :class="{'column-order-dragging': draggedColumn?.key === column.key}"
              >
                <v-list-item-title class="d-flex align-center">
                  <v-icon class="mr-2 drag-handle">mdi-drag</v-icon>
                  <div class="column-color-indicator mr-2" :style="`background-color: ${column.color}`"></div>
                  {{ column.title }}
                  <v-spacer></v-spacer>
                  <v-icon 
                    size="small" 
                    :color="column.visible ? 'primary' : 'grey'"
                    :icon="column.visible ? 'mdi-eye' : 'mdi-eye-off'"
                    class="visibility-toggle"
                    @click="toggleColumnVisibility(column, index)"
                  ></v-icon>
                </v-list-item-title>
              </v-list-item>
            </v-list>
            
            <v-alert
              v-if="!hasVisibleColumns"
              type="warning"
              variant="tonal"
              density="compact"
              class="mt-3 mb-0"
            >
              Selecione pelo menos uma coluna para exibição
            </v-alert>
            
            <v-alert
              v-else
              type="info"
              variant="tonal"
              density="compact"
              class="mt-3 mb-0"
            >
              Todas as alterações são salvas automaticamente
            </v-alert>
          </v-card>
        </v-menu>
      </v-col>
    </v-row>

    <!-- Filtros panel -->
    <v-expansion-panels v-model="filtrosExpanded" class="mb-2">
      <v-expansion-panel>
        <v-expansion-panel-title>
          <v-row no-gutters>
            <div class="d-flex align-center">
              <v-icon class="mr-2">mdi-filter</v-icon>
              <span class="text-subtitle-1">Filtros</span>
            </div>
          </v-row>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-row>
            <!-- Período -->
            <v-col cols="12" md="3">
              <v-text-field
                v-model="assistenciaStore.filtros.dataInicio"
                label="Data de início"
                type="date"
                density="compact"
                hide-details
                class="mb-2"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                v-model="assistenciaStore.filtros.dataFim"
                label="Data de término"
                type="date"
                density="compact"
                hide-details
                class="mb-2"
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="3">
              <v-select
                v-model="assistenciaStore.filtros.origem"
                :items="origemOptions"
                item-title="title"
                item-value="value"
                label="Origem"
                density="compact"
                multiple
                chips
                hide-details
                class="mb-2"
              ></v-select>
            </v-col>
            <v-col cols="12" md="3">
              <v-select
                v-model="assistenciaStore.filtros.estados"
                :items="estadosOptions"
                item-title="title"
                item-value="value"
                label="Estado/UF"
                density="compact"
                multiple
                chips
                hide-details
                prepend-inner-icon="mdi-map-marker"
                class="mb-2"
              ></v-select>
            </v-col>
            
            <!-- Campos de pesquisa -->
            <v-col cols="12" md="3">
              <v-text-field
                v-model="assistenciaStore.filtros.nomeFantasia"
                label="Nome fantasia"
                density="compact"
                hide-details
                prepend-inner-icon="mdi-store"
                class="mb-2"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                v-model="assistenciaStore.filtros.numeroAssistencia"
                label="Número da assistência"
                density="compact"
                hide-details
                prepend-inner-icon="mdi-pound"
                class="mb-2"
              ></v-text-field>
            </v-col>
            
            <v-col cols="12" md="3">
              <v-text-field
                v-model="assistenciaStore.filtros.telefoneCliente"
                v-mask="'(##) #####-####'"
                label="Telefone do cliente"
                density="compact"
                hide-details
                prepend-inner-icon="mdi-phone"
                class="mb-2"
              ></v-text-field>
            </v-col>
            
            <!-- Filter action buttons -->
            <v-col cols="12" class="d-flex justify-end pt-0">
              <v-btn
                color="error"
                variant="text"
                class="mr-2"
                prepend-icon="mdi-refresh"
                @click="limparFiltros"
              >
                Limpar
              </v-btn>
              <v-btn
                color="primary"
                prepend-icon="mdi-filter-check"
                @click="aplicarFiltros"
              >
                Aplicar filtros
              </v-btn>
            </v-col>
          </v-row>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- Loading indicator -->
    <v-progress-linear v-if="assistenciaStore.loading" indeterminate color="primary"></v-progress-linear>

    <!-- Error display -->
    <v-alert v-if="assistenciaStore.error" type="error" closable class="mb-4">
      {{ assistenciaStore.error }}
    </v-alert>

    <!-- Empty state when no columns are visible -->
    <v-card v-if="!hasVisibleColumns" class="my-4 pa-4 text-center">
      <v-icon size="64" color="grey-lighten-1" class="mb-4">mdi-eye-off</v-icon>
      <h3 class="text-h5 mb-2">Nenhuma coluna selecionada</h3>
      <p class="text-body-1 mb-4">Selecione pelo menos uma coluna para visualizar as assistências técnicas</p>
      <v-btn
        color="primary"
        prepend-icon="mdi-eye"
        @click="toggleAllColumns(true)"
      >
        Exibir todas as colunas
      </v-btn>
    </v-card>

    <!-- Kanban board -->
    <div v-else class="kanban-container">
      <div class="kanban-wrapper">
        <AssistenciaTecnicaColumnView
          v-for="column in visibleColumns" 
          :key="column.key"
          :column="column"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Layout */
.v-expansion-panel-title{
  padding: 10px 15px;
}

.kanban-container {
  position: relative;
  width: 100%;
  overflow-x: auto;
  padding-bottom: 12px;
  min-height: calc(100vh - 205px);
}

.kanban-wrapper {
  display: flex;
  min-width: min-content;
  gap: 14px;
}

/* Scrollbars */
.kanban-container::-webkit-scrollbar {
  height: 8px;
  width: 8px;
}

.kanban-container::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 4px;
}

.kanban-container::-webkit-scrollbar-track {
  background-color: rgba(0, 0, 0, 0.05);
}

/* Drag and drop */
.column-order-item {
  cursor: grab;
  transition: all 0.2s;
  border-radius: 4px;
}

.column-order-item:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.column-order-dragging {
  opacity: 0.5;
  background-color: rgba(0, 0, 0, 0.1);
}

.column-color-indicator {
  width: 16px;
  height: 16px;
  border-radius: 4px;
}

.drag-handle {
  cursor: grab;
  opacity: 0.7;
}

.column-order-item:hover .drag-handle {
  opacity: 1;
}

.visibility-toggle {
  cursor: pointer;
  transition: all 0.2s;
  padding: 4px;
  border-radius: 50%;
}

.visibility-toggle:hover {
  background-color: rgba(0, 0, 0, 0.1); 
  transform: scale(1.1);
}

/* Responsive design */
@media (max-width: 768px) {
  .kanban-column {
    flex: 0 0 280px;
    max-width: 280px;
    min-width: 280px;
  }
}
</style>
